import { useEffect, useState, useCallback } from "react";
import { useProject } from "@/contexts/ProjectContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, UserPlus, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const MODULES = [
  { key: "accounts", label: "Accounts" },
  { key: "resources", label: "Resources" },
  { key: "tasks", label: "Tasks" },
  { key: "settings", label: "Settings" },
];

export default function Settings() {
  const { selected, loadProjects } = useProject();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === "owner";

  const [form, setForm] = useState({ name: "", client_name: "", client_contact: "", status: "Active", description: "" });
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ user_id: "", permissions: [] });

  useEffect(() => {
    if (selected) {
      setForm({
        name: selected.name,
        client_name: selected.client_name,
        client_contact: selected.client_contact || "",
        status: selected.status,
        description: selected.description || "",
      });
    }
  }, [selected]);

  const loadMembers = useCallback(async () => {
    if (!selected) return;
    const [m, u] = await Promise.all([
      api.get(`/projects/${selected.id}/members`),
      isOwner ? api.get(`/users`) : Promise.resolve({ data: [] }),
    ]);
    setMembers(m.data);
    setAllUsers(u.data.filter((x) => x.role !== "owner"));
  }, [selected, isOwner]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const saveProject = async () => {
    setSaving(true);
    try {
      await api.patch(`/projects/${selected.id}`, form);
      toast.success("Project updated");
      loadProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async () => {
    if (!confirm(`Delete project "${selected.name}"? This will remove all its accounts, resources, and tasks.`)) return;
    await api.delete(`/projects/${selected.id}`);
    toast.success("Project deleted");
    await loadProjects();
    navigate("/");
  };

  const togglePerm = (perms, key) =>
    perms.includes(key) ? perms.filter((p) => p !== key) : [...perms, key];

  const updateMemberPerms = async (uid, perms) => {
    await api.patch(`/projects/${selected.id}/members/${uid}`, { permissions: perms });
    toast.success("Permissions updated");
    loadMembers();
  };
  const removeMember = async (uid) => {
    if (!confirm("Remove this member?")) return;
    await api.delete(`/projects/${selected.id}/members/${uid}`);
    toast.success("Removed");
    loadMembers();
  };
  const addMember = async () => {
    if (!addForm.user_id) return;
    await api.post(`/projects/${selected.id}/members`, addForm);
    setAddForm({ user_id: "", permissions: [] });
    setAddOpen(false);
    toast.success("Member added");
    loadMembers();
  };

  if (!selected) return <div className="text-sm text-slate-500">Select a project first.</div>;

  const availableUsers = allUsers.filter((u) => !members.some((m) => m.user_id === u.id));

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Project Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Edit details and manage member access</p>
      </div>

      <Card className="rounded-2xl border-slate-200 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <h2 className="font-display text-lg font-semibold">Project Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Project Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name" />
            </div>
            <div className="space-y-2">
              <Label>Client Name</Label>
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="settings-client-name" />
            </div>
            <div className="space-y-2">
              <Label>Client Contact</Label>
              <Input value={form.client_contact} onChange={(e) => setForm({ ...form, client_contact: e.target.value })} data-testid="settings-client-contact" />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="settings-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Paused">Paused</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="settings-description" />
          </div>
          <div className="flex justify-end">
            <Button onClick={saveProject} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="settings-save">
              <Save className="h-4 w-4 mr-2" /> Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {isOwner && (
        <Card className="rounded-2xl border-slate-200 shadow-sm">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">Project Members</h2>
              <Button variant="outline" onClick={() => setAddOpen(true)} disabled={availableUsers.length === 0} data-testid="add-member-button">
                <UserPlus className="h-4 w-4 mr-2" /> Add Member
              </Button>
            </div>
            <div className="space-y-3">
              {members.length === 0 && <div className="text-sm text-slate-500">No members. Only you (owner) have access.</div>}
              {members.map((m) => (
                <div key={m.user_id} className="p-4 rounded-xl border border-slate-200" data-testid={`member-row-${m.user_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-medium text-slate-900">{m.user?.name}</div>
                      <div className="text-xs text-slate-500">{m.user?.email}</div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => removeMember(m.user_id)} data-testid={`remove-member-${m.user_id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {MODULES.map((mod) => {
                      const checked = (m.permissions || []).includes(mod.key);
                      return (
                        <label key={mod.key} className="flex items-center gap-2 text-sm text-slate-700">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => {
                              const next = togglePerm(m.permissions || [], mod.key);
                              updateMemberPerms(m.user_id, next);
                            }}
                            data-testid={`perm-${m.user_id}-${mod.key}`}
                          />
                          {mod.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {isOwner && (
        <Card className="rounded-2xl border-rose-200 shadow-sm bg-rose-50/30">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-rose-600" />
              <h2 className="font-display text-lg font-semibold text-rose-900">Danger Zone</h2>
            </div>
            <p className="text-sm text-slate-600">Permanently delete this project and all its data.</p>
            <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-100" onClick={deleteProject} data-testid="delete-project-button">
              <Trash2 className="h-4 w-4 mr-2" /> Delete Project
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Add Project Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Team Member</Label>
              <Select value={addForm.user_id} onValueChange={(v) => setAddForm({ ...addForm, user_id: v })}>
                <SelectTrigger data-testid="add-member-user"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>
                  {availableUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Permissions</Label>
              <div className="flex flex-wrap gap-3">
                {MODULES.map((mod) => (
                  <label key={mod.key} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={addForm.permissions.includes(mod.key)}
                      onCheckedChange={() => setAddForm({ ...addForm, permissions: togglePerm(addForm.permissions, mod.key) })}
                      data-testid={`add-perm-${mod.key}`}
                    />
                    {mod.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={addMember} data-testid="confirm-add-member">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
